/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react'
import { Link } from 'react-router-dom'
import type { BlogPost } from '../types'
import DatePill from './DatePill'
import Pill from './Pill'
import useLocale from 'js/hooks/useLocale'

interface PostInListProps {
  post: BlogPost
  uiTexts?: Array<{ slug: string; text: string }>
}

const PostInList: React.FC<PostInListProps> = ({ post, uiTexts }) => {
  const { language } = useLocale()
  const byText = uiTexts?.find((text) => text.slug === 'by')?.text || 'by'

  return (
    <article className="post-in-list">
      <Link
        to={`/${language}/blog/${post.slug}`}
        className="post-card-link"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <div style={styles.imageContainer}>
          {post.featuredImage && (
            <img
              src={post.featuredImage.responsiveImage.src}
              srcSet={post.featuredImage.responsiveImage.srcSet}
              sizes={post.featuredImage.responsiveImage.sizes}
              alt={post.featuredImage.responsiveImage.alt || post.title}
              style={styles.image}
              className="post-image"
            />
          )}
        </div>
        <div style={styles.content}>
          <h2 style={styles.title}>{post.title}</h2>
          {post.seo?.description && (
            <p style={styles.excerpt}>{post.seo.description}</p>
          )}
          <div style={styles.meta}>
            <DatePill date={post._firstPublishedAt} />
            {post.authorName && (
              <Pill variant="light">
                {byText} {post.authorName}
              </Pill>
            )}
          </div>
        </div>
      </Link>
    </article>
  )
}

export default PostInList

const styles = {
  imageContainer: {
    position: 'relative' as const,
    overflow: 'hidden',
    aspectRatio: '1/1',
    height: '100%',
  },
  image: {
    margin: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    transition: 'filter var(--transition-base)',
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    paddingTop: 'var(--spacing-md)',
    borderTop: '2px solid var(--color-primary)',
    height: '100%',
    justifyContent: 'space-between',
  },
  title: {
    margin: '0 0 var(--spacing-sm) 0',
    fontSize: 'var(--font-size-xl)',
    fontWeight: 700,
    lineHeight: 'var(--line-height-heading)',
    letterSpacing: '0.5px',
  },
  excerpt: {
    margin: '0 0 var(--spacing-md) 0',
    fontSize: 'var(--font-size-base)',
    lineHeight: '1.2',
    color: 'var(--color-black)',
  },
  meta: {
    marginTop: 'auto',
    display: 'flex',
    gap: 'var(--spacing-sm)',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  byText: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-text-light)',
  },
}
